import React, { FC, useCallback, useMemo } from 'react';

import { Form, Formik } from 'formik';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import { ALL_DROPDOWN_VALUE } from '../../constants/selectCommonOptions';

import useQueryParams from '../../hooks/helper/useQueryParams';

import {
    SearchProps,
    TableFilterConfig,
    TableFilterType,
    ToggleButtonFilterProps,
} from './types';

import { SearchableSelectType } from '../inputs/searchableSelect';
import AampeToggleButton, {
    ToggleButtonProps,
} from '../shared/AampeToggleButton/AampeToggleButton';
import AampeTableSearchNew from '../tables/AampeTableSearchNew';
import MultiSelectFilter from '../tables/filters/MultiSelectFilter';
import SingleSelectFilter from '../tables/filters/SingleSelectFilter';
import FilterChip from './FilterChip';
import SortButtonPopup from './SortButtonPopup';

interface TableFiltersProps {
    tableName?: string;
    filtersConfig?: TableFilterConfig[];
    allowSearching?: boolean;
    allowSorting?: boolean;
    searchProps?: SearchProps;
    handleFilterChange?: (
        item: TableFilterConfig,
        value: string | number | SearchableSelectType[],
    ) => void;
    sortOptions?: Record<string, { sort_by: string; is_sorted_desc: string }>;
    disableFilterChip?: boolean;
    className?: string;
    customActions?: React.ReactNode;
    leftCustomActions?: React.ReactNode;
}

const TableFilters: FC<React.PropsWithChildren<TableFiltersProps>> = ({
    tableName,
    allowSearching,
    allowSorting,
    filtersConfig,
    searchProps,
    handleFilterChange,
    sortOptions,
    disableFilterChip,
    className,
    customActions,
    leftCustomActions,
}) => {
    const filtersState = useQueryParams();

    const getSelectedDropdownValue = useCallback(
        (
            field: string,
            isMulti: boolean,
        ): SearchableSelectType | SearchableSelectType[] | undefined => {
            const config = filtersConfig?.find((item) => item.field === field);
            const options = config?.meta?.options ?? [];

            if (isMulti && filtersState[field]) {
                const selectedValues = (filtersState[field] as any).split(',');
                return options.filter((option) =>
                    selectedValues.includes(option.value),
                );
            }

            // For single select
            if (filtersState[field]) {
                return options.find(
                    (item) => item.value === filtersState[field],
                );
            }

            return options.find((item) => item.value === ALL_DROPDOWN_VALUE);
        },
        [filtersConfig, filtersState],
    );

    const getToggleButtons = useCallback<
        (data: TableFilterConfig) => ToggleButtonProps[]
    >(
        (data: TableFilterConfig) => {
            const config = filtersConfig?.find(
                (item) => item.field === data.field,
            );
            const options = (config?.meta?.options ??
                []) as ToggleButtonFilterProps[];
            return options.map((item) => ({
                title: item.label,
                value: item.value,
                onChange: (value) => handleFilterChange?.(data, value),
                isSelected: filtersState[data.field],
                isLeft: item.isLeft,
                isRight: item.isRight,
                className: item.className,
            }));
        },
        [filtersConfig, filtersState, handleFilterChange],
    );

    const handleMultiSelectChange = useCallback(
        (item: TableFilterConfig, selectedOptions: SearchableSelectType[]) => {
            const values = selectedOptions?.flatMap((option) => option);
            const filterValues = [...new Set(values ?? [])];

            handleFilterChange?.(item, filterValues);
        },
        [handleFilterChange],
    );

    const initialValues = useMemo<
        Record<string, (string | number)[]> | undefined
    >(() => {
        if (!filtersConfig) {
            return undefined; // If there is no filtersConfig, return undefined
        }

        const values: Record<string, (string | number)[]> = {};

        filtersConfig.forEach(({ field }) => {
            const paramValue = filtersState[field];

            // If paramValue is already an array, assign it directly
            if (Array.isArray(paramValue)) {
                values[field] = paramValue;
            } else if (typeof paramValue === 'string') {
                // If paramValue is a string, split it into an array
                values[field] = paramValue.split(',');
            } else if (paramValue) {
                // If paramValue exists but is not a string or array, wrap it in an array
                values[field] = [paramValue];
            } else {
                // If paramValue is undefined or falsy, assign an empty array
                values[field] = [];
            }
        });

        return values;
    }, [filtersConfig, filtersState]);
    return (
        <Container className={className}>
            <Formik
                initialValues={initialValues ?? {}}
                onSubmit={() => {}}
                enableReinitialize
            >
                {() => (
                    <StyledForm>
                        <FilterContent>
                            <Left>
                                {allowSearching && (
                                    <AampeTableSearchNew
                                        placeholder={searchProps?.placeholder}
                                        tableName={tableName}
                                        removeBorderBottom
                                        wrapperStyle={{ marginLeft: '0' }}
                                    />
                                )}
                                {leftCustomActions}
                                <Left>
                                    {filtersConfig?.map(
                                        (item) =>
                                            item.type ===
                                                TableFilterType.MULTISELECT && (
                                                <MultiSelectFilter
                                                    key={item.field}
                                                    item={item}
                                                    onChange={
                                                        handleMultiSelectChange
                                                    }
                                                />
                                            ),
                                    )}
                                </Left>
                                <Left>
                                    {filtersConfig?.map(
                                        (item) =>
                                            item.type ===
                                                TableFilterType.DROPDOWN && (
                                                <SingleSelectFilter
                                                    key={item.field}
                                                    label={item.label ?? ''}
                                                    name={item.field}
                                                    options={
                                                        item.meta?.options ?? []
                                                    }
                                                    value={getSelectedDropdownValue(
                                                        item.field,
                                                        false,
                                                    )}
                                                    onChange={(field, value) =>
                                                        handleFilterChange?.(
                                                            item,
                                                            (
                                                                value as SearchableSelectType
                                                            ).value,
                                                        )
                                                    }
                                                    defaultValue={
                                                        item?.defaultValue
                                                    }
                                                    placeholder={
                                                        item.placeholder
                                                    }
                                                />
                                            ),
                                    )}
                                </Left>
                            </Left>
                            <Right>
                                {filtersConfig?.map((item) => {
                                    if (
                                        item.type ===
                                        TableFilterType.TOGGLE_BUTTONS
                                    ) {
                                        return (
                                            <AampeToggleButton
                                                key={item.field}
                                                buttons={getToggleButtons(item)}
                                                selectedColor={
                                                    aampeTheme.colors.solidA01
                                                }
                                            />
                                        );
                                    }
                                })}
                                {allowSorting && (
                                    <SortButtonPopup
                                        sortOptions={sortOptions ?? {}}
                                    />
                                )}

                                {customActions}
                            </Right>
                        </FilterContent>

                        {!disableFilterChip && (
                            <FilterChip
                                filtersConfig={filtersConfig}
                                getSelectedDropdownValue={
                                    getSelectedDropdownValue
                                }
                                handleFilterChange={handleFilterChange}
                            />
                        )}
                    </StyledForm>
                )}
            </Formik>
        </Container>
    );
};

const StyledForm = styled(Form)({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const FilterContent = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: aampeTheme.spacing4(2),
});

const Container = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: aampeTheme.spacing4(2),
    width: '100%',
}));

const Left = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    flexWrap: 'wrap',
});

const Right = styled.div({
    display: 'flex',
    alignItems: 'start',
    alignSelf: 'end',
    flexWrap: 'wrap',
    gap: aampeTheme.spacing4(2),
});

export default TableFilters;
